import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import Seo from '../components/Seo';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userData = await login(email, password);
      toast.success('Welcome back!');
      // Redirect admins straight to the dashboard; regular users go to `from` or home
      if (userData?.is_admin) {
        navigate('/admin');
      } else {
        navigate(from);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-[#050505] flex items-center justify-center px-4 pt-20"
      data-testid="login-page"
    >
      <Seo title="Sign In" description="Sign in to Unight to manage RSVPs, buy tickets, and access your dashboard." path="/login" noindex />
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-[#0A0A0A] to-[#050505]" />
        <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-[#B5FF00]/5 blur-[100px] rounded-full" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <span className="font-['Josefin_Sans'] text-3xl font-bold text-[#B5FF00]">UNIGHT</span>
          </Link>
          <h1 className="font-['Josefin_Sans'] text-5xl md:text-6xl font-bold text-white mb-2">
            WELCOME BACK
          </h1>
          <p className="text-zinc-400">
            Sign in to access your tickets
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">
              EMAIL
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-12 bg-[#0A0A0A] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none transition-colors"
              placeholder="your@email.com"
              required
              data-testid="login-email"
            />
          </div>

          <div>
            <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">
              PASSWORD
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-12 bg-[#0A0A0A] border border-zinc-800 text-white px-4 pr-12 focus:border-[#B5FF00] focus:outline-none transition-colors"
                placeholder="••••••••"
                required
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="mt-2 text-right">
              <Link
                to="/forgot-password"
                className="font-mono text-xs text-zinc-400 hover:text-[#B5FF00] tracking-wider transition-colors"
                data-testid="forgot-password-link"
              >
                FORGOT PASSWORD?
              </Link>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="login-submit"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                SIGNING IN...
              </>
            ) : (
              'SIGN IN'
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center mt-8 text-zinc-500">
          Don't have an account?{' '}
          <Link 
            to="/signup" 
            className="text-[#B5FF00] hover:underline"
            data-testid="signup-link"
          >
            Sign up
          </Link>
        </p>

      </div>
    </div>
  );
};

export default LoginPage;
